import { Card, CardContent } from '@mui/material'
import { useEffect, useState } from 'react'

import { t } from 'i18next'
import moment from 'moment'
import { NotificationManager } from 'react-notifications'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import LoadingScreen from '../../../../components/custom/LoadingScreen'
import SelectFilter from '../../../../components/dropdowns/select-filter'
import GenericPagination from '../../../../components/generic-table/generic-pagination'
import GenericTable from '../../../../components/generic-table/generic-table'
import { checkIcon, deleteIcon, eyeIcon } from '../../../../components/generic-table/icons'
import TableEmptyData from '../../../../components/table/TableEmptyData'
import accommodationService from '../../../../services/accommodationService'
import { identifyVenue, trackEvent } from '../../../../utils/mixpanelUtil'

export const bookingListText = (text) => t(`accommodation.booking.list.${text}`)

const BookingsList = () => {
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false)
	const [data, setData] = useState([])
	const [filter, setFilter] = useState('All')
	const filterOptions = ['All', 'Pending', 'Processing', 'Confirmed', 'Cancelled', "Completed"]
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)
	const [dropdownOpen, setDropdownOpen] = useState(false)

	const toggleDropdown = () => {
		setDropdownOpen(!dropdownOpen)
	}

	const { short_code } = useSelector((state) => state.app)

	const handleFilterChange = (event) => {
		setFilter(event)
		setPage(0)
	}

	const handleOpen = (row) => {
		navigate(`${row?.id}`)
	}

	const handleDataFetch = (short_code) => {
		let payload = {
			short_code,
		}

		let filterData;

		if (filter === 'All') {
			filterData = {}
		} else {
			filterData = {
				status: filter,
			}
		}
		setLoading(true)
		accommodationService
			.GetBooking(payload, filterData)
			.then((res) => {
				identifyVenue(short_code)
				trackEvent('GetBooking', {
					action_category: 'booking',
					action_outcome: 'success',
					interaction_element: 'booking',
				})
				setLoading(false)
				setData(res?.bookings?.data)
			})
			.catch((err) => {
				identifyVenue(short_code)
				trackEvent('GetBooking', {
					action_category: 'booking',
					action_outcome: 'fail',
					interaction_element: 'booking',
				})
				setLoading(false)
				throw new Error('Problem retrieving the bookings')
			})
	}

	useEffect(() => {
		handleDataFetch(short_code)
	}, [short_code, filter])

	const statusTransformer = {
		Pending: { bgColor: '#f8dda7', textColor: '#94660c', readable: 'Pending' },
		Upcoming: {
			bgColor: '#BCE8F1',
			textColor: '#31708F',
			readable: 'Upcoming',
		},
		Cancelled: { bgColor: '#ff2f2f', textColor: '#fff', readable: 'Canceled' },
		Completed: { bgColor: '#3C763D', textColor: '#fff', readable: 'Completed' },
		Confirmed: { bgColor: '#3C763D', textColor: '#fff', readable: 'Confirmed' },
	}



	const StatusCell = ({ data }) => (
		<div
			style={{
				backgroundColor: `${statusTransformer[data]?.bgColor}`,
			}}
			className={`font-semibold text-[16px] text-cyan1 border align-center rounded-[10px] cursor-pointer px-[16px] w-[120px]`}
		>
			<span
				className={`flex justify-center items-center text-[14px] font-bold p-2`}
				style={{
					color: statusTransformer[data]?.textColor,
				}}
			>
				{statusTransformer[data]?.readable}
			</span>
		</div>
	)

	const RentalUnitCell = ({ data }) => {
		const rentalUnit = JSON.parse(data);
		return <div className='flex w-full items-center text-nowrap'>{rentalUnit?.name}</div>
	}

	const columns = [
		{
			field: 'created_at',
			label: bookingListText('created_at'),
			align: 'right',
		},
		{
			field: 'rental_unit',
			label: bookingListText('apartment'),
			align: 'right',
			cellComponent: RentalUnitCell
		},
		{ field: 'check_in', label: bookingListText('check_in'), align: 'right' },
		{
			field: 'check_out',
			label: bookingListText('check_out'),
			align: 'right',
		},
		{
			field: 'nr_guests',
			label: bookingListText('nr_guests'),
			align: 'right',
		},
		{
			field: 'status',
			label: bookingListText('status'),
			align: 'right',
			cellComponent: StatusCell,
		},
		{
			field: 'paid_with',
			label: bookingListText('paid_with'),
			align: 'right',
		},
		{
			field: 'total_amount',
			label: bookingListText('amount'),
			align: 'right',
			cellComponent: ({ data }) => {
				return <div className='flex w-full items-center justify-center text-nowrap'>{data}
				</div>
			}
		},
		{
			field: 'actions',
			label: bookingListText('actions'),
			align: 'right',
		},
	]

	const transformedData = data?.map((row) => ({
		...row,
		rental_unit: JSON.stringify(row.rental_unit),
		total_amount: `${row?.total_amount} ${row?.rental_unit?.currency}`,
		created_at: moment(row.created_at).format('MMMM DD, YYYY'),
		check_in: row.check_in_date,
		check_out: row.check_out_date,
		nr_guests: row.guest_nr,
		status: row.status,
		paid_with: row.paid_with,
		payment: row,
		actions: row,
	}))

	const onRowsPerPageChange = (event) => {
		setRowsPerPage(Number(event ?? 10))
		setPage(0)
	}

	if (loading) {
		return <LoadingScreen />
	}


	const onDecline = (rowId) => {
		handleStatusChange({ id: rowId, status: "Cancelled" });
	}

	const onConfirm = (rowId) => {
		handleStatusChange({ id: rowId, status: "Confirmed" });
	}

	const handleStatusChange = (payload) => {
		accommodationService
			.ChangeBookingStatus(payload)
			.then((res) => {
				NotificationManager.success(
					`Booking has been ${payload.status} successfully`,
					'Success',
					3000
				)
				handleDataFetch(short_code);
			})
			.catch((err) => {
				console.error(err);
				NotificationManager.error('Failed changing Booking status')
			});
	}

	const customActions = (row) => {
		return (
			<div className='flex items-center gap-2'>
				<div
					className="icon-wrapper cursor-pointer"
					onClick={() => handleOpen(row)}
				>
					<span>{eyeIcon}</span>
				</div>
				{row.status !== "Confirmed" && <div
					className="icon-wrapper cursor-pointer"
					onClick={() => onConfirm(row?.id)}
				>
					<span>{checkIcon}</span>
				</div>}
				{row.status !== "Cancelled" &&
					<div
						className="icon-wrapper cursor-pointer"
						onClick={() => onDecline(row?.id)}
					>
						<span>{deleteIcon}</span>
					</div>
				}
			</div>

		)
	}

	// TODO: add a way to confirm booking
	return (
		<div>
			<div className="flex flex-col h-full p-6">
				<Card>
					<CardContent className="!p-0">
						<div className="flex justify-between items-center px-6 mt-2">
							<div className="flex items-center">
								<span className="text-black1 text-xl pt-2 font-medium">
									{bookingListText('all_bookings')}
								</span>
							</div>

							<SelectFilter
								onChange={handleFilterChange}
								selectedItem={filter}
								toggleDropdown={toggleDropdown}
								dropdownOpen={dropdownOpen}
								items={filterOptions}
								className="!w-48"
							/>
						</div>

						{data.length > 0 ? (
							<>
								<GenericTable
									data={transformedData}
									columns={columns}
									customActions={customActions}
								/>

								<GenericPagination
									onPageChange={(_, newPage) => setPage(newPage)}
									onRowsPerPageChange={onRowsPerPageChange}
									page={page}
									rowsPerPage={rowsPerPage}
									setPage={setPage}
									count={data.length}
								/>
							</>
						) : (
							<TableEmptyData content={bookingListText('booking')} />
						)}
					</CardContent>
				</Card>

				{/* <BookingModal {...{ handleClose, open, selectedRow }} /> */}
			</div>
		</div>
	)
}

export default BookingsList
